import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, ReactNode, useRef } from 'react';
import { View, Text, TouchableOpacity, Animated, Easing, StyleProp, ViewStyle } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FontWeight from '../styles/fontWeights';

interface AccordionItem {
    title: string;
    content: ReactNode;
}

interface AccordionProps {
    items: AccordionItem[];
    selectionMode?: 'single' | 'multiple';
    rowHeight?: number;
    animationDuration?: number;
    tableStyle?: boolean;
    accordionContainerStyle?: StyleProp<ViewStyle>;
    accordionItemStyle?: StyleProp<ViewStyle>;
    accordionItemHeaderStyle?: StyleProp<ViewStyle>;
}

export default function Accordion({ items, selectionMode = 'multiple', rowHeight = 250, animationDuration = 300, accordionContainerStyle, accordionItemStyle, accordionItemHeaderStyle, tableStyle }: AccordionProps) {
    const { styles } = useStyles(styleSheet);

    const [openIndices, setOpenIndices] = useState<number[]>([]);
    const heightAnimations = items.map(() => useRef(new Animated.Value(0)).current);

    const toggleAccordion = (index: number) => {
        const isOpen = openIndices.includes(index);
        let newOpenIndices: number[];

        if (selectionMode == 'single') {
            // Close all others, toggle the clicked one
            newOpenIndices = isOpen ? [] : [index];
        } else {
            // Toggle the clicked one, keep others as they are
            newOpenIndices = isOpen
                ? openIndices.filter((i) => i !== index)
                : [...openIndices, index];
        }

        setOpenIndices(newOpenIndices);

        // Animate the clicked accordion
        Animated.timing(heightAnimations[index], {
            toValue: isOpen ? 0 : rowHeight,
            duration: animationDuration,
            easing: Easing.ease,
            useNativeDriver: false,
        }).start();

        // If singleOpen, close other open accordions
        if (selectionMode == 'single' && !isOpen) {
            openIndices.forEach((openIndex) => {
                if (openIndex !== index) {
                    Animated.timing(heightAnimations[openIndex], {
                        toValue: 0,
                        duration: animationDuration,
                        easing: Easing.ease,
                        useNativeDriver: false,
                    }).start();
                }
            });
        }
    };

    return (
        <View style={accordionContainerStyle ? accordionContainerStyle : styles.container}>
            {items.map((item, index) => {
                return (
                    <View key={index} style={accordionItemStyle ? accordionItemStyle : tableStyle ? styles.itemContainerTableStyle(index == items.length - 1) : styles.itemContainer}>
                        <TouchableOpacity style={accordionItemHeaderStyle ? accordionItemHeaderStyle : styles.header(index % 2 != 0)} onPress={() => toggleAccordion(index)}>
                            <FontAwesomeIcon icon={openIndices.includes(index) ? faChevronDown : faChevronRight} fontSize={16} style={styles.iconColor} />
                            <Text style={styles.title}>{item.title}</Text>
                        </TouchableOpacity>
                        <Animated.View style={[styles.contentContainer, { height: heightAnimations[index] }]}>
                            {openIndices.includes(index) && <View style={styles.content}>{item.content}</View>}
                        </Animated.View>
                    </View>
                );
            })}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        fontFamily: 'Open Sans',
        fontSize: 14,
    },
    itemContainer: {
        borderRadius: 8,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
        marginBottom: 8,
    },
    itemContainerTableStyle: (lastItem: boolean) => ({
        borderBottomWidth: lastItem ? 0 : 1,
        borderBottomColor: theme.colors.dataTable.border,
    }),
    header: (odd: boolean) => ({
        flexDirection: 'row',
        gap: 24,
        alignItems: 'center',
        padding: 16,
        backgroundColor: odd ? theme.colors.dataTable.oddRowBackground : 'transparent',
    }),
    title: {
        fontSize: 14,
        fontWeight: FontWeight.semiBold,
        color: theme.colors.dataTable.rowText,
    },
    icon: {
        width: 24,
        height: 24,
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconColor: {
        color: theme.colors.iconColor,
    },
    contentContainer: {
        overflow: 'hidden',
    },
    content: {
        width: '100%',
        height: '100%'
    },
}));
